import { authConfig } from 'features/agents/helpers/utils';

import { fetcher, Params } from '../../fetcher';
import {
  AdvancedResultsParams,
  JWTData,
  BookDemoParams,
  BookDemoType,
  CompanyList,
  CompanyParams,
  Config,
  ConfirmPasswordParams,
  CountriesParams,
  Country,
  CountryParams,
  DetailsParams,
  FirstList,
  FirstListParam,
  ForgotPasswordData,
  ForgotPasswordParams,
  LoginParams,
  Nomenclature,
  Price,
  PriceParams,
  Product,
  RegisterParams,
  RegisterRegisParams,
  ResultsParams,
  ScoreData,
  ScoreParam,
  SearchDataParams,
  SearchParams,
  SetupData,
  SetupParam,
  SocialAuthData,
  SocialAuthParams,
  SocialInitData,
  SocialInitParams,
  State,
  TrialParams,
  UploadResponse,
  VerifyParams,
} from './dashboard.types';

const baseUrl = 'https://dashboard-api.globaldatabase.com/api';

export const dashboard = {
  // TODO: Move all crm related methods inside "crm" property here
  book: ({ lang, ...params }: BookDemoParams) => {
    const $window = window as any;

    return fetcher.post(
      `${baseUrl}/crm/shop/book`,
      {
        ...params,
        session_key: typeof $window.m_auto_id != 'undefined' ? $window.m_auto_id() : '',
        source: `${window.location.href}#${params.type}`,

        // Uppercase<BookDemoType> doesn't work, it works but can't build
        type: params.type?.toUpperCase() as BookDemoType,
      },
      { lang },
    );
  },

  apply: (params: FormData) => fetcher.post(`${baseUrl}/crm/shop/apply`, params, { lang: '', formData: true }),

  nomenclatures: (params: Params) => fetcher.get<Nomenclature[]>(`${baseUrl}/nomenclature/countries`, params),

  product: {
    get: ({ id, ...params }: Params) => fetcher.get<Product>(`${baseUrl}/applications/products/public/${id}`, params),
  },

  // TODO: Move all credit reports related methods inside "credit reports" property here
  country: (params: Params) => fetcher.get<Country[]>(`${baseUrl}/credit-reports/countries`, params),

  state: (params: Params) => fetcher.get<State[]>(`${baseUrl}/credit-reports/search/states`, params),

  results: (params: ResultsParams) => fetcher.get<CompanyList>(`${baseUrl}/credit-reports/search`, params),

  advanced: (params: AdvancedResultsParams) =>
    fetcher.get<CompanyList>(`${baseUrl}/credit-reports/search/advanced`, params),

  price: (params: PriceParams) => fetcher.get<Price>(`${baseUrl}/billing/credit-reports/price`, params),

  // TODO: Move all billing related methods inside "billing" property here
  investigationPrice: (params: PriceParams) => fetcher.get<Price>(`${baseUrl}/billing/investigation/price`, params),

  trial: ({ product_id: productId, ...params }: TrialParams) =>
    fetcher.post<{ token: string }>(`${baseUrl}/billing/trial/${productId}`, params, {
      lang: '',
    }),

  // TODO: Move all enrichment related methods inside "enrichment" property here
  upload: (params: FormData) =>
    fetcher.post<UploadResponse>(`${baseUrl}/enrichment/companies/session/upload`, params, {
      lang: '',
      formData: true,
    }),

  setup: ({ session_key: sessionKey, ...params }: SetupParam) =>
    fetcher.post<SetupData>(`${baseUrl}/enrichment/companies/session/${sessionKey}/setup`, params, { lang: '' }),

  firstList: ({ session_key: sessionKey, ...params }: FirstListParam) =>
    fetcher.get<FirstList>(`${baseUrl}/enrichment/companies/session/${sessionKey}/list/first`, params),

  score: ({ session_key: sessionKey, ...params }: ScoreParam) =>
    fetcher.get<ScoreData>(`${baseUrl}/enrichment/companies/session/${sessionKey}/score`, params),

  config: (params: Params) => fetcher.get<Config>(`${baseUrl}/enrichment/companies/config`, params),

  auth: {
    login: ({ ...params }: LoginParams) => fetcher.post<JWTData>(`${baseUrl}/auth/jwt/create`, params, { lang: '' }),

    register: ({ app, ...params }: RegisterParams) =>
      fetcher.post<JWTData>(`${baseUrl}/register/${app}`, params, { lang: '' }),

    registerRegis: (params: RegisterRegisParams) =>
      fetcher.post<void>(`${baseUrl}/auth/jwt/register/regis`, params, { lang: '' }),

    verify: (params: VerifyParams) => fetcher.post<JWTData>(`${baseUrl}/auth/jwt/verify`, params, { lang: '' }),

    reset: ({ ...params }: ForgotPasswordParams) =>
      fetcher.post<ForgotPasswordData>(`${baseUrl}/auth/jwt/password/reset`, params, { lang: '' }),

    confirm: ({ ...params }: ConfirmPasswordParams) =>
      fetcher.post<JWTData>(`${baseUrl}/auth/jwt/password/reset/confirm`, params, { lang: '' }),

    details: (params: DetailsParams) => fetcher.get<void>(`${baseUrl}/profile/details`, { ...params, lang: '' }),
  },

  socialAuth: {
    init: ({ social, ...params }: SocialInitParams) =>
      fetcher.get<SocialInitData>(`${baseUrl}/auth/social/${social}`, {
        ...params,
        lang: '',
        options: {
          credentials: 'include',
        },
      }),

    auth: ({ state, code, social, ...params }: SocialAuthParams) =>
      fetcher.post<SocialAuthData>(
        `${baseUrl}/auth/social/${authConfig.AGENTS_APP}/${social}?state=${state}&code=${code}`,
        params,
        {
          lang: '',
          options: {
            credentials: 'include',
          },
        },
      ),
  },

  agents: {
    countries: ({ type, ...params }: CountriesParams) =>
      fetcher.get<CountryParams[]>(`${baseUrl}/agent-ai/countries/${type}`, params),

    search: ({ headers, type, ...params }: SearchParams) =>
      fetcher.post<SearchDataParams[]>(`${baseUrl}/agent-ai/company/search/${type}`, params, { headers, lang: '' }),

    company: ({ id, type, ...params }: CompanyParams) =>
      fetcher.get<Record<string, string>>(`${baseUrl}/agent-ai/company/${id}/${type}`, { ...params, lang: '' }),
  },
};
